import type { LocalStorage, StoredList } from "../types/index.js";
import {
  backupStorage as backupStorageApi,
  restoreStorage as restoreStorageApi,
  syncPush,
} from "./api.js";
import { STORAGE_KEY } from "./constants.js";

const SYNC_KEY = "tohdoh_sync";

function defaults(): LocalStorage {
  return { displayName: "", lists: [] };
}

function isStoredList(v: unknown): v is StoredList {
  if (!v || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.listId === "string" &&
    typeof o.shareToken === "string" &&
    typeof o.name === "string" &&
    typeof o.isPrimary === "boolean" &&
    typeof o.joinedAt === "string"
  );
}

function read(): LocalStorage {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaults();
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return defaults();

    const displayName = typeof parsed.displayName === "string" ? parsed.displayName : "";
    const lists = Array.isArray(parsed.lists) ? parsed.lists.filter(isStoredList) : [];

    return { displayName, lists };
  } catch {
    return defaults();
  }
}

function write(data: LocalStorage): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  backupToServer(data);
  pushToSyncAccount(data);
}

/** Fire-and-forget backup to server-set httpOnly cookie (survives ITP). */
function backupToServer(data: LocalStorage): void {
  backupStorageApi(data.displayName, data.lists).catch(() => {
    // Best-effort — failure doesn't affect normal operation
  });
}

/** Fire-and-forget push to sync account if linked. */
function pushToSyncAccount(data: LocalStorage): void {
  const syncAccountId = getSyncAccountId();
  if (!syncAccountId) return;
  syncPush(syncAccountId, data.displayName, data.lists).catch(() => {
    // Best-effort — failure doesn't affect normal operation
  });
}

export function getDisplayName(): string {
  return read().displayName;
}

export function setDisplayName(name: string): void {
  const data = read();
  data.displayName = name;
  write(data);
}

export function getLists(): StoredList[] {
  return read().lists;
}

export function getPrimaryList(): StoredList | undefined {
  const lists = getLists();
  return lists.find((l) => l.isPrimary) ?? lists[0];
}

export function addList(list: Omit<StoredList, "isPrimary" | "joinedAt">): StoredList {
  const data = read();
  // Check if already exists
  const existing = data.lists.find((l) => l.listId === list.listId);
  if (existing) {
    // Update shareToken in case it was rotated
    existing.shareToken = list.shareToken;
    existing.name = list.name;
    write(data);
    return existing;
  }

  const newList: StoredList = {
    ...list,
    isPrimary: data.lists.length === 0,
    joinedAt: new Date().toISOString(),
  };
  data.lists.push(newList);
  write(data);
  return newList;
}

export function removeList(listId: string): void {
  const data = read();
  const wasPrimary = data.lists.find((l) => l.listId === listId)?.isPrimary;
  data.lists = data.lists.filter((l) => l.listId !== listId);
  if (wasPrimary && data.lists.length > 0) {
    data.lists[0]!.isPrimary = true;
  }
  write(data);
}

export function setPrimary(listId: string): void {
  const data = read();
  data.lists.forEach((l) => {
    l.isPrimary = l.listId === listId;
  });
  write(data);
}

export function updateListShareToken(listId: string, newShareToken: string): void {
  const data = read();
  const list = data.lists.find((l) => l.listId === listId);
  if (list) {
    list.shareToken = newShareToken;
    write(data);
  }
}

/** Attempt to restore data from server cookie backup. Returns restored data or null. */
export async function restoreFromServer(): Promise<LocalStorage | null> {
  try {
    const data = await restoreStorageApi();
    if (!data) return null;
    write({ displayName: data.displayName, lists: data.lists });
    return { displayName: data.displayName, lists: data.lists };
  } catch {
    return null;
  }
}

// ── Sync account management ──

export function getSyncAccountId(): string | null {
  try {
    const raw = localStorage.getItem(SYNC_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.syncAccountId === "string" ? parsed.syncAccountId : null;
  } catch {
    return null;
  }
}

export function setSyncAccountId(id: string): void {
  localStorage.setItem(SYNC_KEY, JSON.stringify({ syncAccountId: id }));
}

export function clearSyncAccountId(): void {
  localStorage.removeItem(SYNC_KEY);
}

/**
 * Apply remote sync data directly to localStorage.
 * Bypasses write() to avoid infinite loop: write → push → onSnapshot → write → ...
 */
export function applyRemoteSync(data: LocalStorage): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  backupToServer(data);
}
